import clsx from 'clsx';
import { useEffect, useMemo, useRef } from 'react';

import { parseMarkdown, type MarkdownSpan } from '../chat/markdown';
import type { ConversationId } from '../wire';
import { useLiveWire } from '../wire';
import { useWorldModel } from '../world';
import {
  selectOrderedConversations,
  useDms,
  type DmMessage,
} from './store';
import styles from './dms.module.css';

/**
 * Memo-wrap the ordered list so zustand's reference-equality comparison
 * doesn't treat every render as a change (the raw selector returns a new
 * array every call — an infinite-loop trap).
 */
function useOrderedConversations(): ReturnType<typeof selectOrderedConversations> {
  const map = useDms((s) => s.conversations);
  return useMemo(
    () => selectOrderedConversations({ conversations: map } as never),
    [map],
  );
}

const MESSAGE_LIMIT = 2000;

/**
 * DM pane — conversations list + active-conversation view + compose.
 * Rendered inside the shell's right-sidebar when expanded.
 *
 * Pagination (`FetchMessagesIntent`), edit + soft-delete UI, add-member
 * and leave controls are baseline-queued — the store shape already
 * supports them, the compose + list + read-state core lands here.
 */
export function DmPane(): JSX.Element {
  const conversations = useOrderedConversations();
  const messagesMap = useDms((s) => s.messages);
  const unread = useDms((s) => s.unread);
  const activeId = useDms((s) => s.activeConversationId);
  const composeText = useDms((s) => s.composeText);
  const setActive = useDms((s) => s.setActiveConversation);
  const setCompose = useDms((s) => s.setComposeText);
  const appendOptimistic = useDms((s) => s.appendOptimistic);
  const selfPlayerId = useWorldModel((s) => s.selfPlayerId);
  const send = useLiveWire((s) => s.send);

  const active = activeId ? useDms.getState().conversations.get(activeId) : null;
  const activeMessages = activeId ? messagesMap.get(activeId) ?? [] : [];

  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [activeMessages.length, activeId]);

  const handleSend = (): void => {
    const text = composeText.trim().slice(0, MESSAGE_LIMIT);
    if (!text || !activeId || selfPlayerId === null) return;

    const sent = send({
      plane: 'Gateway',
      intent: {
        kind: 'DmSend',
        data: {
          // P1.8 C5 boundary: the wire carries `ConversationId = number`
          // but the client DMs store keys conversations with strings. The
          // two-way bridge is a separate carry-forward (pre-existing drift
          // since P1.7); this cast preserves today's runtime behaviour
          // without expanding the C5 refactor's scope.
          conversation_id: activeId as unknown as ConversationId,
          content: text,
        },
      },
    });
    if (!sent) return; // rate-limited; throttle pushed a toast
    appendOptimistic({
      id: `local-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      conversation_id: activeId,
      sender: selfPlayerId,
      content: text,
      sent_at: 0n,
      edited: false,
      deleted: false,
    });
    setCompose('');
  };

  return (
    <div className={styles.pane}>
      <ConversationList
        conversations={conversations}
        activeId={activeId}
        unread={unread}
        onSelect={setActive}
      />
      {active ? (
        <ActiveConversation
          conversation={active}
          messages={activeMessages}
          selfPlayerId={selfPlayerId}
          composeText={composeText}
          onComposeChange={setCompose}
          onSend={handleSend}
          scrollRef={scrollRef}
        />
      ) : (
        <div className={styles.empty}>
          {conversations.length === 0
            ? 'No conversations yet. When DMs arrive, they appear here.'
            : 'Select a conversation to read messages.'}
        </div>
      )}
    </div>
  );
}

function ConversationList({
  conversations,
  activeId,
  unread,
  onSelect,
}: {
  conversations: ReturnType<typeof selectOrderedConversations>;
  activeId: string | null;
  unread: Record<string, number>;
  onSelect: (id: string) => void;
}): JSX.Element {
  if (conversations.length === 0) {
    return <div className={styles.listEmpty}>No DMs</div>;
  }
  return (
    <nav className={styles.list} aria-label="DM conversations">
      {conversations.map((c) => {
        const isActive = c.id === activeId;
        const unreadCount = unread[c.id] ?? 0;
        return (
          <button
            type="button"
            key={c.id}
            className={clsx(styles.listItem, isActive && styles.listItemActive)}
            onClick={() => onSelect(c.id)}
            aria-pressed={isActive}
            aria-label={conversationLabel(c)}
          >
            <span className={styles.listItemName}>
              {conversationLabel(c)}
            </span>
            {unreadCount > 0 ? (
              <span className={styles.unreadBadge}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            ) : null}
          </button>
        );
      })}
    </nav>
  );
}

function ActiveConversation({
  conversation,
  messages,
  selfPlayerId,
  composeText,
  onComposeChange,
  onSend,
  scrollRef,
}: {
  conversation: ReturnType<typeof selectOrderedConversations>[number];
  messages: DmMessage[];
  selfPlayerId: bigint | null;
  composeText: string;
  onComposeChange: (text: string) => void;
  onSend: () => void;
  scrollRef: React.MutableRefObject<HTMLDivElement | null>;
}): JSX.Element {
  return (
    <div className={styles.active}>
      <header className={styles.header}>
        <div className={styles.headerTitle}>
          {conversationLabel(conversation)}
        </div>
        <div className={styles.headerMembers}>
          {conversation.members.length} members
        </div>
      </header>
      <div ref={scrollRef} className={styles.scroll} role="log" aria-live="polite">
        {messages.length === 0 ? (
          <div className={styles.noMessages}>No messages yet. Say hi.</div>
        ) : (
          messages.map((m) => (
            <MessageRow
              key={m.id}
              message={m}
              isSelf={selfPlayerId !== null && m.sender === selfPlayerId}
            />
          ))
        )}
      </div>
      <form
        className={styles.composeRow}
        onSubmit={(e) => {
          e.preventDefault();
          onSend();
        }}
      >
        <input
          type="text"
          value={composeText}
          onChange={(e) => onComposeChange(e.target.value)}
          placeholder={`Message @${conversationLabel(conversation)}`}
          maxLength={MESSAGE_LIMIT}
          className={styles.composeInput}
          aria-label="DM compose"
        />
        <button
          type="submit"
          className={styles.composeButton}
          disabled={composeText.trim().length === 0}
        >
          Send
        </button>
      </form>
    </div>
  );
}

function MessageRow({
  message,
  isSelf,
}: {
  message: DmMessage;
  isSelf: boolean;
}): JSX.Element {
  if (message.deleted) {
    return (
      <div className={styles.tombstone}>
        [deleted]
      </div>
    );
  }
  return (
    <div
      className={clsx(
        styles.row,
        isSelf && styles.rowSelf,
        message.pending && styles.rowPending,
      )}
    >
      <span className={styles.author}>
        {isSelf ? 'You' : `player-${message.sender}`}
      </span>
      <span className={styles.body}>
        <InlineMarkdown text={message.content} />
        {message.edited ? (
          <span className={styles.editedTag} title="edited">
            {' '}
            (edited)
          </span>
        ) : null}
      </span>
    </div>
  );
}

function InlineMarkdown({ text }: { text: string }): JSX.Element {
  const spans = useMemo<MarkdownSpan[]>(() => parseMarkdown(text), [text]);
  return (
    <>
      {spans.map((span, idx) => {
        switch (span.kind) {
          case 'bold':
            return <strong key={idx}>{span.text}</strong>;
          case 'italic':
            return <em key={idx}>{span.text}</em>;
          case 'code':
            return (
              <code key={idx} className={styles.inlineCode}>
                {span.text}
              </code>
            );
          default:
            return <span key={idx}>{span.text}</span>;
        }
      })}
    </>
  );
}

function conversationLabel(
  c: { name: string | null; members: bigint[] },
): string {
  if (c.name) return c.name;
  // Fallback: list the member player ids. The first-non-self member is
  // the counterpart in 1:1; we don't know self at this layer so display
  // all members. The header already shows the count.
  return c.members.map((m) => `player-${m}`).join(', ');
}
